import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Pencil, Send, Ban, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { emailCampaignService } from '@/services/emailCampaignService'
import { OverviewTab, RecipientsTab, LinksTab, DeliverabilityTab, EmailContentTab } from '@/components/MarketingEmail/CampaignAnalytics'
import { formatDateTime } from '@/lib/dateUtils'

const statusVariant = {
  DRAFT: 'secondary',
  SCHEDULED: 'default',
  SENDING: 'default',
  SENT: 'secondary',
  CANCELLED: 'outline',
  FAILED: 'outline',
}

const CampaignDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [sendingNow, setSendingNow] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [confirmSendOpen, setConfirmSendOpen] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)

  const { data, isLoading } = useQuery(['email-campaign', id], () => emailCampaignService.getCampaignById(id), {
    onError: () => toast.error('Failed to fetch campaign'),
    refetchInterval: (data) => (['SENDING'].includes(data?.data?.campaign?.status) ? 3000 : false),
  })
  const campaign = data?.data?.campaign

  const refresh = () => queryClient.invalidateQueries(['email-campaign', id])

  const handleSendNow = async () => {
    setSendingNow(true)
    try {
      await emailCampaignService.sendNow(id)
      toast.success('Campaign send completed')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send campaign')
    } finally {
      setSendingNow(false)
      setConfirmSendOpen(false)
    }
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      const res = await emailCampaignService.cancelCampaign(id)
      toast.success(res?.message || 'Campaign cancelled')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel campaign')
    } finally {
      setCancelling(false)
      setConfirmCancelOpen(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!campaign) {
    return <div className="text-center py-12 text-muted-foreground">Campaign not found.</div>
  }

  const editable = ['DRAFT', 'SCHEDULED'].includes(campaign.status)
  const cancellable = ['DRAFT', 'SCHEDULED', 'SENDING'].includes(campaign.status)

  return (
    <div className="space-y-6">
      <PageHeader
        title={campaign.name}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/campaigns')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {editable && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/marketing-email/campaigns/${id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            {cancellable && (
              <Button variant="outline" size="sm" onClick={() => setConfirmCancelOpen(true)}>
                <Ban className="mr-2 h-4 w-4" />
                {campaign.status === 'SENDING' ? 'Stop Send' : 'Cancel'}
              </Button>
            )}
            {editable && (
              <Button size="sm" onClick={() => setConfirmSendOpen(true)}>
                <Send className="mr-2 h-4 w-4" />
                Send Now
              </Button>
            )}
          </>
        }
      />

      <StatCardGroup>
        <StatCard
          title="Status"
          value={<Badge variant={statusVariant[campaign.status] || 'secondary'} className={campaign.status === 'SENT' ? 'bg-blue-50 text-blue-700 font-medium hover:bg-blue-50' : undefined}>{campaign.status}</Badge>}
          valueClassName="text-base font-medium leading-none"
          contentClassName="p-4"
        />
        <StatCard title="Recipients" value={campaign.recipient_count ?? campaign.segment?.last_evaluated_count ?? '—'} contentClassName="p-4" />
        <StatCard title="Audience" value={campaign.segment?.name || '—'} contentClassName="p-4" />
        <StatCard title="Template" value={campaign.template?.name || (campaign.template_id ? '—' : 'Custom (no template)')} contentClassName="p-4" />
      </StatCardGroup>

      {['SENT', 'SENDING'].includes(campaign.status) && (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="recipients">Recipients</TabsTrigger>
            <TabsTrigger value="links">Links</TabsTrigger>
            <TabsTrigger value="deliverability">Deliverability</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab campaignId={id} /></TabsContent>
          <TabsContent value="recipients"><RecipientsTab campaignId={id} /></TabsContent>
          <TabsContent value="links"><LinksTab campaignId={id} /></TabsContent>
          <TabsContent value="deliverability"><DeliverabilityTab campaignId={id} /></TabsContent>
          <TabsContent value="email"><EmailContentTab campaign={campaign} /></TabsContent>
        </Tabs>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subject</span><span className="font-medium">{campaign.template_snapshot?.subject_line || campaign.template?.subject_line || '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Scheduled for</span><span className="font-medium">{campaign.scheduled_at ? formatDateTime(campaign.scheduled_at) : '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Sent at</span><span className="font-medium">{campaign.sent_at ? formatDateTime(campaign.sent_at) : '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Created by</span><span className="font-medium">{campaign.creator?.email || '—'}</span></div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Campaign Now</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately send "{campaign.name}" to everyone in the "{campaign.segment?.name}" segment. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendNow} disabled={sendingNow}>
              {sendingNow && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{campaign.status === 'SENDING' ? 'Stop Send' : 'Cancel Campaign'}</AlertDialogTitle>
            <AlertDialogDescription>
              {campaign.status === 'SENDING'
                ? 'This campaign is currently sending. Recipients who have already been emailed will not be un-sent, but remaining recipients will be skipped. Continue?'
                : 'Are you sure you want to cancel this campaign?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Never mind</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} disabled={cancelling}>
              {cancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {campaign.status === 'SENDING' ? 'Stop Send' : 'Cancel Campaign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default CampaignDetail
